import defaultOptions from '@object-generators/wood-connection/defaultOptions';
import AbstractGenerator from '@scene/misc/generator';
import Beam from '@scene/objects/beam';
import Ground from '@scene/objects/ground';
import NailPlate from '@scene/objects/nail/nail-plate';
import TextLabel from '@scene/objects/text';
import ObjectView from '@scene/views/object-view';
import * as THREE from 'three';
import WoodConnectionObject from '../wood-connection.object';

export default class CornerConnection extends AbstractGenerator {

  static GROUND_CENTER_OFFSET = 100;
  static DEFAULT_GROUND_DIMENSIONS = {
    WIDTH: 200,
    HEIGHT: 100,
    DEPTH: 200
  };

  constructor(options, dispatcher) {
    super(options, defaultOptions);
    this.dispatchOptionUpdate = dispatcher;
  }

  init2DObject() {
    this.generate();
    this.layer2D.addMesh(this.mesh);
    this.layer2D.htmlLayer.addLayer(this.htmlLayer);
  }

  init3DObject() {
    this.generate();
    this.layer3D.addMesh(this.mesh);
    this.layer3D.htmlLayer.addLayer(this.htmlLayer);
  }

  setOrientation() {
    super.setOrientation();

    if (this.is2DMode) {
      this.layer2D.mesh.rotation.set(-Math.PI / 2, -Math.PI / 2, 0);
    } else {
      this.layer3D.mesh.rotation.set(0, 0, 0);
    }
  }

  generate() {
    const { a, b, c, d } = this.options.panel.dimensions || {};
    const { wood } = this.options.panel;
    const verticalPlateThickness = this.options.panel.thickness.tfixA || defaultOptions.plate.tfixA;
    const horizontalPlateThickness = this.options.panel.thickness.tfixB || defaultOptions.plate.tfixB;
    const verticalBeamHeight = a + horizontalPlateThickness + WoodConnectionObject.VERTICAL_BEAM_HEIGHT_OFFSET;
    const labelsOptions = {
      is3DMode: this.is3DMode,
      rotation: this.layer3D.mesh.rotation,
      showLabels: this.options.mode === ObjectView.mode.MODE_2D || this.options.object.dimensions.showLabels,
      displayHorizontally: this.options.object.dimensions.displayHorizontally
    };

    const nailPlate = new NailPlate({
      ...this.options,
      rotation: new THREE.Euler(0, 0, 0)
    });

    const ground = new Ground({
        type: this.options.ground.type,
        width: this.options.ground.corner.width,
        height: this.options.ground.corner.height,
        depth: this.options.ground.corner.depth,
        padding: this.options.ground.padding
      },
      nailPlate.screwGroup.screwCoordinates,
      {
        ...this.options,
        panel: {
          ...this.options.panel,
          // width: this.options.panel.height,
          height: this.options.panel.height + 50, // increase the size of the ground to make space for the back wood
        },
        ground: {
          ...this.options.ground,
          maxDepth: this.options.ground.straight.depth
        }
      }
    );
    ground.onOptionUpdate(this.dispatchOptionUpdate.bind(this));
    ground.mesh.rotation.z = Math.PI / 2;
    ground.mesh.rotation.y = Math.PI / 2;
    this.mesh.add(ground.mesh);

    if (this.is3DMode) {
      const { edgeLabels } = ground.getEdgeDistanceLabels(labelsOptions);
      const labelsYOffset = ground.size.depth + horizontalPlateThickness / 2 + wood.thicknessE1 + wood.thicknessE2;
      // C1x
      edgeLabels.C1x.object.rotation.z = Math.PI / 2;
      edgeLabels.C1x.object.rotation.y = Math.PI / 2;
      edgeLabels.C1x.object.position.y = labelsYOffset;
      edgeLabels.C1x.text.position.set(
        edgeLabels.C1x.text.position.x,
        edgeLabels.C1x.text.position.y + labelsYOffset,
        edgeLabels.C1x.text.position.z,
      );
      edgeLabels.C1x.text.setDirection(
        edgeLabels.C1x.text.position.clone().setZ(-50),
        edgeLabels.C1x.text.position.clone().setZ(50),
        false
      );
      this.mesh.add(edgeLabels.C1x.object);
      this.htmlLayer.add(edgeLabels.C1x.text);

      // C1y
      edgeLabels.C1y.object.rotation.z = Math.PI / 2;
      edgeLabels.C1y.object.rotation.y = Math.PI / 2;
      edgeLabels.C1y.object.position.y = labelsYOffset;
      edgeLabels.C1y.text.position.set(
        edgeLabels.C1y.text.position.x,
        edgeLabels.C1y.text.position.y + labelsYOffset,
        edgeLabels.C1y.text.position.z,
      );
      edgeLabels.C1y.text.setDirection(
        edgeLabels.C1y.text.position.clone().setX(-50),
        edgeLabels.C1y.text.position.clone().setX(50),
        false
      );
      this.mesh.add(edgeLabels.C1y.object);
      this.htmlLayer.add(edgeLabels.C1y.text);
      
      // C2y
      edgeLabels.C2y.object.rotation.z = Math.PI / 2;
      edgeLabels.C2y.object.rotation.y = Math.PI / 2;
      edgeLabels.C2y.object.position.y = labelsYOffset;
      edgeLabels.C2y.text.position.set(
        edgeLabels.C2y.text.position.x,
        edgeLabels.C2y.text.position.y + labelsYOffset,
        edgeLabels.C2y.text.position.z,
      );
      edgeLabels.C2y.text.setDirection(
        edgeLabels.C2y.text.position.clone().setX(-50),
        edgeLabels.C2y.text.position.clone().setX(50),
        false
      );
      this.mesh.add(edgeLabels.C2y.object);
      this.htmlLayer.add(edgeLabels.C2y.text);
    }

    nailPlate.mesh.position.set(
      -b / 2,
      ground.size.depth + wood.thicknessE1 + wood.thicknessE2 + horizontalPlateThickness / 2,
      0
    );

    const frontWoodPadding = 50;
    const backWoodPadding = 30;
    const horizontalBeamLength = b + horizontalPlateThickness + frontWoodPadding + 3 * backWoodPadding;
    const horizontalBeam = new Beam({
      type: Beam.types.SHAPE,
      width: wood.L,
      height: wood.thicknessE1,
      color: defaultOptions.materials.woodColor,
      shape: {
        outer: [
          { x: -wood.L / 2, y: -horizontalBeamLength / 2 },
          { x: wood.L / 2, y: -horizontalBeamLength / 2 },
          { x: wood.L / 2, y: horizontalBeamLength / 2 },
          { x: -wood.L / 2, y: horizontalBeamLength / 2 }
        ]
      }
    });
    horizontalBeam.mesh.rotation.y = Math.PI / 2;
    horizontalBeam.mesh.position.set(
      (frontWoodPadding - 3 * backWoodPadding) / 2,
      ground.size.depth + horizontalBeam.size.height / 2,
      nailPlate.mesh.position.z
    );
    this.mesh.add(horizontalBeam.mesh);

    const topHorizontalBeamLength = b + horizontalPlateThickness + frontWoodPadding + backWoodPadding;
    const topHorizontalBeam = new Beam({
      type: Beam.types.SHAPE,
      width: wood.L,
      height: wood.thicknessE2,
      color: defaultOptions.materials.secondaryWoodColor,
      shape: {
        outer: [
          { x: -wood.L / 2, y: -topHorizontalBeamLength / 2 },
          { x: wood.L / 2, y: -topHorizontalBeamLength / 2 },
          { x: wood.L / 2, y: topHorizontalBeamLength / 2 },
          { x: -wood.L / 2, y: topHorizontalBeamLength / 2 }
        ]
      }
    });
    topHorizontalBeam.mesh.rotation.y = Math.PI / 2;
    topHorizontalBeam.mesh.position.set(
      (frontWoodPadding - backWoodPadding) / 2,
      ground.size.depth + horizontalBeam.size.height + topHorizontalBeam.size.height / 2,
      nailPlate.mesh.position.z
    );
    this.mesh.add(topHorizontalBeam.mesh);

    const verticalBeamDepth = backWoodPadding;
    const verticalBeam1 = new Beam({
      type: Beam.types.SHAPE,
      width: wood.L,
      height: verticalBeamHeight,
      color: defaultOptions.materials.secondaryWoodColor,
      shape: {
        outer: [
          { x: -verticalBeamDepth / 2, y: -wood.L / 2 },
          { x: verticalBeamDepth / 2, y: -wood.L / 2 },
          { x: verticalBeamDepth / 2, y: wood.L / 2 },
          { x: -verticalBeamDepth / 2, y: wood.L / 2 }
        ]
      }
    });
    verticalBeam1.mesh.position.set(
      topHorizontalBeam.mesh.position.z -b / 2 - verticalPlateThickness / 2 - backWoodPadding / 2,
      ground.size.depth +
      horizontalBeam.size.height +
      topHorizontalBeam.size.height +
      verticalBeam1.size.height / 2,
      horizontalBeam.mesh.position.z 
    );
    this.mesh.add(verticalBeam1.mesh);

    const htmlLayerOffset = {
      x: -verticalBeam1.mesh.position.z,
      y: 0,
      z: -ground.size.depth - horizontalBeam.size.height - topHorizontalBeam.size.height - horizontalPlateThickness / 2
    };

    if (this.is2DMode) {
      let labels = [ ...nailPlate.labels.cLabels, ...nailPlate.labels.sLabels, 
        nailPlate.labels.plateDimensions.ALabel, nailPlate.labels.plateDimensions.CLabel, nailPlate.labels.plateDimensions.DLabel];
      labels.forEach(label => {
        if (label && label.text) {
          label.text.position.set(
            label.text.position.x + htmlLayerOffset.x,
            label.text.position.y + htmlLayerOffset.y,
            label.text.position.z + htmlLayerOffset.z
          );
        }
      });
    } else if (this.options.object.dimensions.display) { // 3D
      nailPlate.labels.cLabels.forEach(label => {
        label.text.position.set(
          nailPlate.mesh.position.x + verticalPlateThickness / 2,
          -label.text.position.z - htmlLayerOffset.z,
          -label.text.position.x + verticalBeam1.mesh.position.z// + htmlLayerOffset.x
        );
      });
      nailPlate.labels.sLabels.forEach(label => {
        label.text.position.set(
          nailPlate.mesh.position.x + verticalPlateThickness / 2,
          -label.text.position.z - htmlLayerOffset.z,
          -label.text.position.x + verticalBeam1.mesh.position.z// + htmlLayerOffset.x
        );
      });
      
      // plate dimension A
      nailPlate.labels.plateDimensions.ALabel.text.position.set(
        nailPlate.mesh.position.x,
        ground.size.depth + horizontalPlateThickness + a / 2 + wood.thicknessE1 + wood.thicknessE2,
        - nailPlate.labels.plateDimensions.ALabel.text.position.x + verticalBeam1.mesh.position.z - 10 // small offset due to the new align
      );
      nailPlate.labels.plateDimensions.ALabel.text.optimalCameraDist = 3000;
      nailPlate.labels.plateDimensions.ALabel.text.zoomFactor = 0.5;
      nailPlate.labels.plateDimensions.ALabel.text.align = TextLabel.types.align.CENTER;
      nailPlate.labels.plateDimensions.ALabel.text.setDirection(
        nailPlate.labels.plateDimensions.ALabel.text.position.clone().setY(-a / 2),
        nailPlate.labels.plateDimensions.ALabel.text.position.clone().setY(a / 2),
        false
      );
      
      // plate dimension B
      nailPlate.labels.plateDimensions.BLabel.text.position.set(
        nailPlate.mesh.position.x + b / 2 - verticalPlateThickness / 2, 
        ground.size.depth + horizontalPlateThickness / 2 + wood.thicknessE1 + wood.thicknessE2,
        -nailPlate.labels.plateDimensions.BLabel.text.position.x + verticalBeam1.mesh.position.z - 10 // small offset
      );
      nailPlate.labels.plateDimensions.BLabel.text.optimalCameraDist = 3000;
      nailPlate.labels.plateDimensions.BLabel.text.zoomFactor = 0.5;
      nailPlate.labels.plateDimensions.BLabel.text.setDirection(
        nailPlate.labels.plateDimensions.BLabel.text.position.clone().setX(-b / 2),
        nailPlate.labels.plateDimensions.BLabel.text.position.clone().setX(b / 2),
        false
      );

      // plate dimension C
      nailPlate.labels.plateDimensions.CLabel.text.position.set(
        nailPlate.mesh.position.x,
        ground.size.depth + wood.thicknessE1 + wood.thicknessE2 + horizontalPlateThickness / 2 
          - nailPlate.labels.plateDimensions.CLabel.text.position.z,
        verticalBeam1.mesh.position.z
      );
      nailPlate.labels.plateDimensions.CLabel.text.optimalCameraDist = 3000;
      nailPlate.labels.plateDimensions.CLabel.text.zoomFactor = 0.5;
      nailPlate.labels.plateDimensions.CLabel.text.setDirection(
        nailPlate.labels.plateDimensions.CLabel.text.position.clone().setZ(-c / 2),
        nailPlate.labels.plateDimensions.CLabel.text.position.clone().setZ(c / 2),
        false
      );

      // plate dimension D 
      nailPlate.labels.plateDimensions.DLabel.text.position.set(
        nailPlate.mesh.position.x + b - verticalPlateThickness / 2 + nailPlate.labels.plateDimensions.DLabel.text.position.z + 10,
        ground.size.depth + horizontalPlateThickness / 2 + wood.thicknessE1 + wood.thicknessE2,
        verticalBeam1.mesh.position.z
      );
      nailPlate.labels.plateDimensions.DLabel.text.optimalCameraDist = 3000;
      nailPlate.labels.plateDimensions.DLabel.text.zoomFactor = 0.5;
      nailPlate.labels.plateDimensions.DLabel.text.setDirection(
        nailPlate.labels.plateDimensions.DLabel.text.position.clone().setZ(-d / 2),
        nailPlate.labels.plateDimensions.DLabel.text.position.clone().setZ(d / 2),
        false
      );
    }

    nailPlate.onOptionUpdate(this.dispatchOptionUpdate);
    this.mesh.add(nailPlate.mesh);
    this.htmlLayer.addLayer(nailPlate.htmlLayer);

    if (this.is3DMode && this.options.object.loads.display) {
      let initialPos = new THREE.Vector3(
        nailPlate.mesh.position.x,
        nailPlate.mesh.position.y + a + horizontalPlateThickness / 2,
        nailPlate.mesh.position.z
      );
      const {dashedLine, arrow, label } = WoodConnectionObject.createLoads(
        initialPos, this.options.panel.loads.faxd, this.dispatchOptionUpdate
      );
      this.mesh.add(dashedLine.mesh);
      this.mesh.add(arrow.mesh);
      this.htmlLayer.add(label);
    }

    return this.mesh;
  }
  
  get materials() {
    return {
      wood: new THREE.MeshLambertMaterial({
        color: defaultOptions.materials.secondaryWoodColor,
        side: THREE.DoubleSide
      })
    };
  }

  get mesh() {
    return this.object.mesh;
  }

} 



// WEBPACK FOOTER //
// ./src/object-view/generators/wood-connection/objects/corner-connection.js